import { Platform, View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IS_WEB, parseApiError } from '@utils';
import { screens, ScreensNameType } from '@navigation';
import { LanguageCodesEnum } from '@constants';
import {
  BaseText,
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  FlagIconSvg,
  Icon,
  Line,
  Separator,
  SettingsLayout,
  Touchable,
} from '@components';
import { LocalizationService, ToastService } from '@services';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useNavOptionsSet } from '@hooks';

const InnerContainer = styled(View)({
  flexGrow: 1,
  ...Platform.select({
    native: {
      justifyContent: 'space-between',
      pb: '$10',
    },
  }),
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: '$6',
  pr: '$2',
});

const Text = styled(BaseText)({
  variant: 'text.medium',
  pl: '$5',
});

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Language,
];

export const LanguageScreen = observer(() => {
  const { t } = useTranslation();
  const initLang = LocalizationService.language;
  const [selectedLangCode, setSelectedLangCode] = useState(initLang);
  const viewer = useViewerModel();

  useNavOptionsSet({ title: t('language.title') });

  function cancelChanges() {
    setSelectedLangCode(initLang);
  }

  async function saveChanges() {
    LocalizationService.changeLanguage(selectedLangCode);

    try {
      await viewer.update.run({
        settings: {
          language: selectedLangCode,
        },
      });
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  const isButtonsShown = initLang !== selectedLangCode;

  return (
    <SettingsLayout
      title={t('language.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <InnerContainer>
        <View>
          {Object.values(LanguageCodesEnum).map((code, index) => (
            <Fragment key={index}>
              {index !== 0 && <Line />}

              <TouchableContainer
                onPress={() => setSelectedLangCode(code)}
              >
                <RowContainer>
                  <FlagIconSvg langCode={code} />
                  <Text>{t(`common.languages.${code}`)}</Text>
                </RowContainer>

                {selectedLangCode === code && (
                  <Icon
                    name="done-circule-bold"
                    color="$primary300"
                    size={20}
                  />
                )}
              </TouchableContainer>
            </Fragment>
          ))}

          {IS_WEB && (
            <Fragment>
              <Line />
              <Separator size="$8" />
            </Fragment>
          )}
        </View>

        {isButtonsShown && (
          <ButtonsLayout>
            <Button onPress={saveChanges}>
              {t('language.save')}
            </Button>

            <Button
              buttonStyle={cancelButtonStyle}
              onPress={cancelChanges}
            >
              {t('language.cancel')}
            </Button>
          </ButtonsLayout>
        )}
      </InnerContainer>
    </SettingsLayout>
  );
});
